import { useState } from "react";
import { ArrowRight, CheckCircle2, MapPin, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Container, Reveal } from "@/components/Motion";
import { CONTACT_PLACEHOLDERS, IMAGES, OM_SERVICE, SERVICES } from "@/data/content";

const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), OM_SERVICE.title, "Other"];

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-evo-blue focus:ring-2 focus:ring-blue-600/15";
const labelCls = "mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600";
const errCls = "mt-1.5 text-xs font-medium text-red-500";

const initialForm = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your business email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select a service.";
    if (!form.message.trim()) er.message = "Please tell us about your project.";
    return er;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="Contact EVOVLEX IT | Let's Start a Conversation"
        description="Have a technology challenge or a new project in mind? Connect with EVOVLEX IT and discover how the right technology can transform your business."
      />
      <PageHero
        eyebrow="Contact Us"
        titleLines={[
          "Let's Start a",
          { node: <span className="text-gradient-brand">Conversation</span> },
        ]}
        sub="Have a technology challenge or a new project in mind? Connect with EVOVLEX IT and discover how the right technology can transform your business."
        image={IMAGES.hero}
      />

      <section data-testid="contact-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
                  {submitted ? (
                    <div className="flex flex-col items-center py-14 text-center" data-testid="contact-success-state">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
                        <CheckCircle2 className="h-9 w-9" strokeWidth={1.6} />
                      </span>
                      <h2 className="mt-6 font-display text-2xl font-bold text-slate-900">Thank you, {form.name.split(" ")[0]}.</h2>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                        Your enquiry about <span className="font-semibold text-slate-900">{form.service}</span> has
                        been noted. Our team will review your message and get back to you.
                      </p>
                      <button
                        type="button"
                        data-testid="contact-send-another-button"
                        onClick={() => {
                          setForm(initialForm);
                          setSubmitted(false);
                        }}
                        className="mt-8 rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition-colors hover:border-evo-blue hover:text-evo-blue"
                      >
                        Send Another Enquiry
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Send an Enquiry</h2>
                      <p className="mt-2 text-sm text-slate-500">
                        Fields marked <span className="text-evo-orange">*</span> are required.
                      </p>
                      <form onSubmit={onSubmit} noValidate className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="contact-enquiry-form">
                        <div>
                          <label htmlFor="contact-name" className={labelCls}>
                            Name <span className="text-evo-orange">*</span>
                          </label>
                          <input
                            id="contact-name"
                            data-testid="contact-name-input"
                            type="text"
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Your full name"
                            className={inputCls}
                            aria-invalid={!!errors.name}
                          />
                          {errors.name && <p className={errCls} data-testid="contact-name-error">{errors.name}</p>}
                        </div>
                        <div>
                          <label htmlFor="contact-company" className={labelCls}>Company Name</label>
                          <input
                            id="contact-company"
                            data-testid="contact-company-input"
                            type="text"
                            value={form.company}
                            onChange={set("company")}
                            placeholder="Your organization"
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className={labelCls}>
                            Business Email <span className="text-evo-orange">*</span>
                          </label>
                          <input
                            id="contact-email"
                            data-testid="contact-email-input"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            placeholder="you@company.com"
                            className={inputCls}
                            aria-invalid={!!errors.email}
                          />
                          {errors.email && <p className={errCls} data-testid="contact-email-error">{errors.email}</p>}
                        </div>
                        <div>
                          <label htmlFor="contact-phone" className={labelCls}>Phone Number</label>
                          <input
                            id="contact-phone"
                            data-testid="contact-phone-input"
                            type="tel"
                            value={form.phone}
                            onChange={set("phone")}
                            placeholder="+00 00000 00000"
                            className={inputCls}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label htmlFor="contact-service" className={labelCls}>
                            Service Interested In <span className="text-evo-orange">*</span>
                          </label>
                          <select
                            id="contact-service"
                            data-testid="contact-service-select"
                            value={form.service}
                            onChange={set("service")}
                            className={`${inputCls} ${form.service ? "" : "text-slate-400"}`}
                            aria-invalid={!!errors.service}
                          >
                            <option value="" disabled>
                              Select a service
                            </option>
                            {SERVICE_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                          {errors.service && <p className={errCls} data-testid="contact-service-error">{errors.service}</p>}
                        </div>
                        <div className="sm:col-span-2">
                          <label htmlFor="contact-message" className={labelCls}>
                            Message <span className="text-evo-orange">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            data-testid="contact-message-input"
                            rows={5}
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Tell us about your project or challenge..."
                            className={`${inputCls} resize-y`}
                            aria-invalid={!!errors.message}
                          />
                          {errors.message && <p className={errCls} data-testid="contact-message-error">{errors.message}</p>}
                        </div>
                        <div className="sm:col-span-2">
                          <button
                            type="submit"
                            data-testid="contact-form-submit-button"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-cyan-500/30 sm:w-auto"
                          >
                            Send an Enquiry
                            <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="relative overflow-hidden rounded-2xl bg-navy-950 p-8 text-white grain sm:p-10">
                  <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
                  <div className="relative">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-400">
                      EVOVLEX IT
                    </p>
                    <p className="mt-3 font-display text-xl font-bold">Technology | Security | Innovation</p>
                    <ul className="mt-8 space-y-6" data-testid="contact-info-list">
                      <li className="flex items-start gap-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-700 text-cyan-400">
                          <Phone className="h-4 w-4" strokeWidth={1.8} />
                        </span>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone Number</p>
                          <p className="mt-1 text-sm" data-testid="contact-phone-placeholder">
                            <a href="tel:+917706355112" className="text-slate-300 transition-colors hover:text-cyan-400">
                              {CONTACT_PLACEHOLDERS.phone}
                            </a>
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-700 text-cyan-400">
                          <MapPin className="h-4 w-4" strokeWidth={1.8} />
                        </span>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Office Address</p>
                          <p className="mt-1 text-sm text-slate-300" data-testid="contact-address-placeholder">
                            {CONTACT_PLACEHOLDERS.address}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
